<template>
  <Modal v-model="isVisible" width="lg">
    <div slot="header">
      Não quero indicar
    </div>

    <template v-if="!finished">
      <div class="warning-box mb-3">
        <strong>Atenção!</strong><br>
        <strong>{{ indications.length }}</strong> multa(s) serão encerradas como
        <strong>Não indicado por decisão do cliente</strong> e nenhum condutor será indicado para elas.
        <br>Enquanto o prazo de indicação não vencer, a decisão pode ser desfeita nos detalhes da multa.
      </div>

      <div v-if="indications.length > 0" class="tickets-box mb-3">
        <h6 class="mb-2 tickets-title">Multas que serão marcadas ({{ indications.length }}):</h6>
        <div class="tickets-scroll">
          <small v-for="indication in indications" :key="indication.id" class="d-block mb-1 ticket-detail">
            • AIT: <strong>{{ ticketValue(indication, 'ait') }}</strong> - Placa: <strong>{{ ticketValue(indication, 'plate') }}</strong>
          </small>
        </div>
      </div>

      <div v-if="ignored.length > 0" class="tickets-box mb-3">
        <h6 class="mb-2 tickets-title">Multas ignoradas ({{ ignored.length }}):</h6>
        <div class="tickets-scroll">
          <small v-for="item in ignored" :key="item.indication.id" class="d-block mb-1 ticket-detail">
            • AIT: <strong>{{ ticketValue(item.indication, 'ait') }}</strong> - {{ item.reason }}
          </small>
        </div>
      </div>

      <label for="decline-reason">Motivo (opcional)</label>
      <textarea
        id="decline-reason"
        v-model="reason"
        class="form-control"
        rows="3"
        :maxlength="maxLength"
        :disabled="processing"
        placeholder="Descreva por que não deseja indicar o condutor"
      ></textarea>
      <div class="text-right">
        <small class="text-muted">{{ reason.length }}/{{ maxLength }}</small>
      </div>

      <p v-if="processing" class="mt-3 mb-0">
        Processando {{ processedCount }} de {{ indications.length }}...
      </p>
    </template>

    <template v-else>
      <div v-if="succeeded.length > 0" class="success-box mb-3">
        <strong>{{ succeeded.length }}</strong> multa(s) marcada(s) como <strong>Não indicado por decisão do cliente</strong>.
      </div>

      <div v-if="failed.length > 0" class="tickets-box">
        <h6 class="mb-2 tickets-title">Não foi possível marcar {{ failed.length }} multa(s):</h6>
        <div class="tickets-scroll">
          <small v-for="item in failed" :key="item.indication.id" class="d-block mb-1 ticket-detail">
            • AIT: <strong>{{ ticketValue(item.indication, 'ait') }}</strong> - {{ item.message }}
          </small>
        </div>
      </div>
    </template>

    <div slot="footer">
      <template v-if="!finished">
        <button class="btn btn-outline mr-2" :disabled="processing" @click="close">
          Cancelar
        </button>
        <button class="btn btn-primary" :disabled="processing || indications.length === 0" @click="confirm">
          Confirmar ({{ indications.length }})
        </button>
      </template>
      <button v-else class="btn btn-primary" @click="close">
        Fechar
      </button>
    </div>
  </Modal>
</template>

<script>
import Modal from '@/components/shares/specialTag/Modal'
import { DECLINE_REASON_MAX_LENGTH } from '@/utils/driverIndicationStatus'

const defaultErrorMessage = 'Ops.. Ocorreu um erro! Tente novamente.'

const errorMessage = error => {
  const errors = error && error.errors
  if (Array.isArray(errors) && errors.length > 0) return errors.join(' ')
  return (typeof errors === 'string' && errors) || defaultErrorMessage
}

const initialState = () => ({
  reason: '',
  processing: false,
  finished: false,
  succeeded: [],
  failed: []
})

export default {
  name: 'DeclineIndicationModal',
  components: { Modal },
  props: {
    value: { type: Boolean, default: false },
    indications: { type: Array, default: () => [] },
    ignored: { type: Array, default: () => [] }
  },
  data: () => ({
    ...initialState(),
    maxLength: DECLINE_REASON_MAX_LENGTH
  }),
  computed: {
    isVisible: {
      get() {
        return this.value
      },
      set() {
        this.close()
      }
    },
    processedCount() {
      return this.succeeded.length + this.failed.length
    }
  },
  watch: {
    value(visible) {
      if (visible) Object.assign(this.$data, initialState())
    }
  },
  methods: {
    ticketValue(indication, key) {
      return (indication.ticket && indication.ticket[key]) || '-'
    },
    async confirm() {
      const reason = this.reason.trim() || null
      this.processing = true

      for (const indication of this.indications) {
        await this.axios.put(`maestro/driver_indications/${indication.id}/decline`, { reason })
          .then(() => this.succeeded.push(indication))
          .catch(e => this.failed.push({ indication, message: errorMessage(e) }))
      }

      this.processing = false
      this.finished = true
    },
    close() {
      if (this.processing) return

      this.$emit('input', false)
      if (this.finished) this.$emit('finished', { succeeded: this.succeeded, failed: this.failed })
    }
  }
}
</script>

<style scoped>
.warning-box {
  background-color: var(--alert-warning-bg);
  border: 1px solid var(--alert-warning-border);
  border-radius: 8px;
  padding: 16px;
  color: var(--alert-warning-text);
}

.success-box {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 8px;
  padding: 16px;
  color: #155724;
}

[data-theme="dark"] .success-box {
  background-color: #1e3a2f;
  border-color: #2a5a45;
  color: #75d4a0;
}

.tickets-box {
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 16px;
}

.tickets-title {
  color: var(--primary-text-color);
  font-weight: 600;
}

.tickets-scroll {
  max-height: 160px;
  overflow-y: auto;
}

.ticket-detail {
  color: var(--terciary-text-color);
}

textarea {
  resize: vertical;
}
</style>
